/**
 * Points Purchase Modal Component
 * Modal for purchasing points without page navigation
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PointsPurchaseTerms from './PointsPurchaseTerms';

const POINT_OPTIONS = [
  { amount: 1000 as const, price: 1000, label: '1,000pt', bonus: 0 },
  { amount: 3000 as const, price: 3000, label: '3,000pt', bonus: 0 },
  { amount: 5000 as const, price: 5000, label: '5,000pt', bonus: 500 },
  { amount: 10000 as const, price: 10000, label: '10,000pt', bonus: 1500 },
  { amount: 30000 as const, price: 30000, label: '30,000pt', bonus: 5000 },
  { amount: 50000 as const, price: 50000, label: '50,000pt', bonus: 10000 },
];

interface PointsPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPurchaseSuccess?: () => void;
  currentPoints: number | null;
  onPurchase: (amount: 1000 | 3000 | 5000 | 10000 | 30000 | 50000) => Promise<void>;
}

export default function PointsPurchaseModal({
  isOpen,
  onClose,
  onPurchaseSuccess,
  currentPoints,
  onPurchase
}: PointsPurchaseModalProps) {
  const { t } = useTranslation('common');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<{ text: string } | { key: string } | null>(null);
  const [selectedAmount, setSelectedAmount] = useState<1000 | 3000 | 5000 | 10000 | 30000 | 50000 | null>(null);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);

  const handlePurchase = async () => {
    if (!selectedAmount) return;

    try {
      setPurchasing(true);
      setPurchaseSuccess(false);
      setError(null);
      setIsLoading(true);

      await onPurchase(selectedAmount);

      setPurchaseSuccess(true);
      setSelectedAmount(null);

      // Notify parent component of successful purchase
      if (onPurchaseSuccess) {
        onPurchaseSuccess();
      }

      setTimeout(() => {
        setPurchaseSuccess(false);
        onClose();
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? { text: err.message } : { key: 'points.purchase.error' });
    } finally {
      setPurchasing(false);
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="relative bg-white shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-br from-pink-50 to-pink-100 text-pink-800 px-6 py-4 flex items-center justify-between border-b border-pink-200">
            <h2 className="text-xl font-bold flex items-center">
              <span className="mr-2">💰</span>
              {t('points.purchase.title')}
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center bg-white hover:bg-pink-50 border border-pink-200 text-pink-700 text-xl font-bold transition-colors"
              aria-label={t('points.purchase.close')}
            >
              ×
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Subtitle */}
            <div className="text-center mb-6">
              <p className="text-gray-600">
                {t('points.purchase.subtitle')}
              </p>
            </div>

            {/* Current Balance */}
            <div className="mb-6">
              <div className="bg-yellow-50 p-5 shadow-md border border-yellow-200">
                <div className="text-center">
                  <div className="text-gray-700 text-sm mb-2 font-medium">{t('points.purchase.currentBalance')}</div>
                  <div className="text-4xl font-bold text-yellow-600">
                    {isLoading ? (
                      <span className="animate-pulse">...</span>
                    ) : (
                      <span>{currentPoints?.toLocaleString() || 0} pt</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Purchase Options */}
            <div className="mb-6">
              <h3 className="text-lg font-bold mb-4 text-center text-gray-800">
                {t('points.purchase.selectAmount')}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {POINT_OPTIONS.map((option) => (
                  <button
                    key={option.amount}
                    onClick={() => setSelectedAmount(option.amount)}
                    className={`relative p-4 border-2 transition-all ${
                      selectedAmount === option.amount
                        ? 'border-pink-400 bg-pink-50 shadow-md'
                        : 'border-gray-200 bg-white hover:border-pink-300 hover:shadow'
                    }`}
                  >
                    <div className="text-2xl font-bold text-pink-700 mb-1">
                      {option.label}
                    </div>
                    <div className="text-gray-600 text-sm font-medium">{t('points.taxIncludedPrice', { amount: option.price.toLocaleString('en-US') })}</div>
                    {option.bonus > 0 && (
                      <div className="absolute top-2 right-2 bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-1">
                        +{option.bonus}pt
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Purchase Button */}
            <div className="mb-6 rounded border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
              <PointsPurchaseTerms showUsage legalLink={
                <a href="/law" className="mt-2 inline-block text-pink-700 underline">{t('points.terms.legalLink')}</a>
              } />
            </div>

            {/* Purchase Button */}
            <div className="mb-6">
              <button
                onClick={handlePurchase}
                disabled={!selectedAmount || purchasing}
                className={`w-full py-3 px-6 font-bold text-lg transition-all ${
                  !selectedAmount || purchasing
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-pink-400 hover:bg-pink-500 text-white shadow-md hover:shadow-lg hover:-translate-y-0.5'
                }`}
              >
                {purchasing ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {t('points.purchase.processing')}
                  </span>
                ) : (
                  selectedAmount ? t('points.purchase.purchaseButton', { amount: selectedAmount.toLocaleString() }) : t('points.purchase.selectPoints')
                )}
              </button>
            </div>

            {/* Success Message */}
            {purchaseSuccess && (
              <div className="mb-6">
                <div className="bg-success-50 border border-success p-4 text-center">
                  <div className="text-success-700 font-semibold">
                    ✅ {t('points.purchase.success')}
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-6">
                <div className="bg-red-50 border border-red-500 p-4 text-center">
                  <div className="text-red-600 font-semibold">⚠️ {'key' in error ? t(error.key) : error.text}</div>
                </div>
              </div>
            )}

            {/* Close Button */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={onClose}
                className="w-full py-3 px-6 bg-gray-400 hover:bg-gray-500 text-white font-bold text-lg transition-colors"
              >
                {t('buttons.close')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
